import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import type { IncidentType, LocalizedString } from '@/types';

import IncidentIcon from '@/assets/icons/incident.svg?react';
import RouteIcon from '@/assets/icons/route-list.svg?react';
import StationIcon from '@/assets/icons/station-list.svg?react';
import DetailRow from '@/components/Incidents/incident-details/detail-row';
import IncidentTiming from '@/components/Incidents/incident-details/incident-timing';
import InlineListButton from '@/components/shared/inline-list-button';
import IncidentDetailsLoader from '@/components/shared/loader/incident-details-loader';
import StatusMessage from '@/components/shared/status-message';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useGetIncidentListData } from '@/hooks/use-get-incident-list-data';
import useMobileLandscape from '@/hooks/use-mobile-landscape';
import useChainStore from '@/store/useChainStore';
import { ERROR_FEEDBACK, UNKNOWN_CAUSE, UNKNOWN_EFFECT } from '@/utils/constants';
import {
  SEARCH_PARAM_INCIDENT_ID,
  SEARCH_PARAM_INCIDENTS,
  SEARCH_PARAM_JOURNEY_ID,
  SEARCH_PARAM_ROUTE_LINE_NUMBER,
  SEARCH_PARAM_SHOW,
  SEARCH_PARAM_STATION_ID,
  SEARCH_PARAM_STATIONS,
  SEARCH_PARAMS_ROUTE_ID,
} from '@/utils/constants/queries';
import { getTranslatedCause } from '@/utils/helpers/get-translated-cause';

const IncidentDetails = () => {
  const { i18n, t } = useTranslation();
  const currentLanguage = i18n.resolvedLanguage as keyof LocalizedString;
  const isMobileLandscape = useMobileLandscape();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToLast } = useChainStore();
  const incidentId = searchParams.get(SEARCH_PARAM_INCIDENT_ID);

  const { data: allIncidents = [], isLoading, isError, refetch } = useGetIncidentListData();

  const incident = useMemo(
    () => allIncidents.find((inc: IncidentType) => inc.gtfsAlertId === incidentId),
    [allIncidents, incidentId]
  );

  const formattedRoutes = useMemo(() => (incident?.affectedRoutes ?? []).join(', '), [incident]);

  const affectedStops = useMemo(() => incident?.affectedStops ?? [], [incident]);

  const handleStopClick = (stopId: string) => {
    if (!stopId) {
      return;
    }

    const newParams = new URLSearchParams(searchParams);

    addToLast({
      [SEARCH_PARAM_SHOW]: newParams.get(SEARCH_PARAM_SHOW),
      [SEARCH_PARAM_INCIDENT_ID]: newParams.get(SEARCH_PARAM_INCIDENT_ID),
      [SEARCH_PARAM_INCIDENTS]: newParams.get(SEARCH_PARAM_INCIDENTS),
      [SEARCH_PARAM_STATION_ID]: newParams.get(SEARCH_PARAM_STATION_ID),
      [SEARCH_PARAMS_ROUTE_ID]: newParams.get(SEARCH_PARAMS_ROUTE_ID),
      [SEARCH_PARAM_ROUTE_LINE_NUMBER]: newParams.get(SEARCH_PARAM_ROUTE_LINE_NUMBER),
      [SEARCH_PARAM_JOURNEY_ID]: newParams.get(SEARCH_PARAM_JOURNEY_ID),
    });

    newParams.delete(SEARCH_PARAM_INCIDENT_ID);
    newParams.delete(SEARCH_PARAM_INCIDENTS);
    newParams.delete(SEARCH_PARAMS_ROUTE_ID);
    newParams.delete(SEARCH_PARAM_ROUTE_LINE_NUMBER);
    newParams.delete(SEARCH_PARAM_JOURNEY_ID);

    newParams.set(SEARCH_PARAM_STATION_ID, stopId);
    newParams.set(SEARCH_PARAM_SHOW, SEARCH_PARAM_STATIONS);

    setSearchParams(newParams);
  };

  if (isLoading) {
    return <IncidentDetailsLoader />;
  }

  if (isError) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center py-8">
        <StatusMessage
          type="error"
          title={t(ERROR_FEEDBACK.general)}
          description={t(ERROR_FEEDBACK.incidents.api)}
          showReloadButton
          onRetry={refetch}
        />
      </div>
    );
  }

  if (!incident) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center py-8">
        <StatusMessage
          type="empty"
          title={t(ERROR_FEEDBACK.incidents.empty)}
          description={t(ERROR_FEEDBACK.incidents.search)}
        />
      </div>
    );
  }

  const { cause, effect, activePeriod, headerText, descriptionText } = incident;

  const hasCause = cause !== UNKNOWN_CAUSE;
  const hasEffect = effect !== UNKNOWN_EFFECT;
  const hasAffectedRoutes = formattedRoutes.length > 0;
  const hasAffectedStops = affectedStops.length > 0;

  const content = (
    <article className="flex w-full flex-col items-start gap-4 px-4 py-4 opacity-100">
      <div className="flex items-center justify-center rounded-lg bg-secondary p-2">
        <IncidentIcon className="size-6" aria-hidden="true" />
      </div>

      <div className="flex flex-col gap-2">
        <h2 className="text-xl font-semibold text-foreground">{headerText[currentLanguage]}</h2>
        <IncidentTiming periods={activePeriod} />
        <p className="mt-2 text-sm font-normal text-foreground wrap-anywhere">
          {descriptionText[currentLanguage]}
        </p>
      </div>

      <dl className="flex flex-col gap-3 w-full">
        {hasCause && (
          <DetailRow label={t('incidents.cause')} value={getTranslatedCause(t, cause)} />
        )}
        {hasEffect && <DetailRow label={t('incidents.effect')} value={effect} />}
        {hasAffectedRoutes && (
          <DetailRow icon={RouteIcon} value={<span className="px-1.5">{formattedRoutes}</span>} />
        )}
        {hasAffectedStops && (
          <DetailRow
            icon={StationIcon}
            value={
              <span className="text-sm font-normal text-foreground">
                {affectedStops.map(({ id, name }: { id: string; name: string }, index: number) => (
                  <InlineListButton key={id} index={index} onClick={() => handleStopClick(id)}>
                    {name}
                  </InlineListButton>
                ))}
              </span>
            }
          />
        )}
      </dl>
    </article>
  );

  if (isMobileLandscape) {
    return <ScrollArea className="h-full">{content}</ScrollArea>;
  }

  return (
    <div className="flex flex-col h-full">
      <ScrollArea className="flex-1 min-h-0">{content}</ScrollArea>
    </div>
  );
};

export default IncidentDetails;
